import React, { useEffect, useState } from 'react';
import {
  Modal,
  Descriptions,
  Button,
  Space,
  Tag,
  Rate,
  List,
  Avatar,
  Input,
  Card,
  Row,
  Col,
  Typography,
  Progress,
  Checkbox,
  Badge,
  Tabs,
  Form,
  DatePicker,
  Table,
  Select,
  InputNumber
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  MessageOutlined,
  UserOutlined,
  CalendarOutlined,
  FlagOutlined,
  TagOutlined,
  StarOutlined,
  PaperClipOutlined,
  DownloadOutlined,
  PlusOutlined,
  DeleteFilled,
  ExpandOutlined,
  CompressOutlined,
  CopyOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  DollarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTaskDetails } from './hooks/useTaskDetails';
import { PRIORITY_CONFIG, Equipment as EQUIPMENT_LIST } from './constants';
import { formatDate, getRelativeTime } from './utils';
import FileUploader from './components/FileUploader';
import { message } from 'antd';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const TaskDetailsModalRefactored = ({ visible, task, onClose, onEdit, onDelete }) => {
  const [modalWidth, setModalWidth] = useState(1100);
  const [isMobile, setIsMobile] = useState(false);
  const [usageEntries, setUsageEntries] = useState([]);
  const [costEntries, setCostEntries] = useState([]);
  const [usageForm] = Form.useForm();
  const [costForm] = Form.useForm();
  const [noteModalVisible, setNoteModalVisible] = useState(false);
  const [currentNote, setCurrentNote] = useState('');
  const [currentNoteIndex, setCurrentNoteIndex] = useState(null);
  const [costNoteModalVisible, setCostNoteModalVisible] = useState(false);
  const [currentCostNote, setCurrentCostNote] = useState('');
  const [currentCostNoteIndex, setCurrentCostNoteIndex] = useState(null);

  const equipmentOptions = React.useMemo(() => {
    return (EQUIPMENT_LIST || []).map(item => ({
      label: `${item.Title}${item?.model?.name ? ' • ' + item.model.name : ''}${item?.model?.category ? ' • ' + item.model.category : ''}`,
      value: item.ID
    }));
  }, []);

  const equipmentValueToLabel = React.useMemo(() => {
    const map = new Map();
    (EQUIPMENT_LIST || []).forEach(item => {
      const label = `${item.Title}${item?.model?.name ? ' • ' + item.model.name : ''}${item?.model?.category ? ' • ' + item.model.category : ''}`;
      map.set(item.ID, label);
    });
    return map;
  }, []);

  const equipmentIdToName = React.useMemo(() => {
    const map = new Map();
    (EQUIPMENT_LIST || []).forEach(item => {
      map.set(item.ID, item.Title);
    });
    return map;
  }, []);

  const {
    comments,
    newComment,
    activeTab,
    subtasks,
    newSubtask,
    descriptionExpanded,
    taskStats,
    addComment,
    setNewComment,
    addSubtask,
    deleteSubtask,
    toggleSubtask,
    editSubtask,
    setNewSubtask,
    setTab,
    toggleDescriptionExpanded,
    copyDescription
  } = useTaskDetails(task);

  useEffect(() => {
    if (!task) return;
  }, [task]);

  useEffect(() => {
    const handleResize = () => {
      const vw = typeof window !== 'undefined' ? window.innerWidth : 1100;
      setModalWidth(Math.min(1100, Math.max(360, vw - 24)));
      setIsMobile(vw <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDelete = () => {
    Modal.confirm({
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
          <span>Delete Task</span>
        </div>
      ),
      content: (
        <div>
          <div style={{ fontSize: '16px', marginBottom: '8px' }}>
            Are you sure you want to delete this task?
          </div>
          <div style={{ background: '#fff2f0', padding: '12px', borderRadius: '6px', border: '1px solid #ffccc7' }}>
            <Text strong style={{ color: '#ff4d4f' }}>
              "{task.content}"
            </Text>
            <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
              This action cannot be undone and all task data will be permanently lost.
            </div>
          </div>
        </div>
      ),
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        onDelete(task.id);
        onClose();
      },
      width: 500
    });
  };

  const handleSubmitUsage = (values) => {
    console.log('Form values:', values); // برای دیباگ
    
    // فیلتر کردن ردیف‌های خالی
    const validItems = (values.items || []).filter(item => 
      item.equipment && item.date
    );

    if (validItems.length === 0) {
      message.warning('لطفاً حداقل یک ردیف معتبر با تجهیز و تاریخ پر کنید');
      return;
    }

    const items = validItems.map((it, idx) => ({
      id: `${Date.now()}_${idx}`,
      equipment: it.equipment,
      equipmentName: equipmentIdToName.get(it.equipment) || it.equipment,
      quantity: it.quantity || 1,
      date: it.date ? it.date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      notes: it.notes || '',
    }));

    setUsageEntries((prev) => [...items, ...prev]);
    usageForm.resetFields();
    message.success('اطلاعات استفاده از تجهیزات ذخیره شد');
  };

  const handleSubmitCost = (values) => {
    const validItems = (values.items || []).filter(item => 
      item.cost && item.date
    );

    if (validItems.length === 0) {
      message.warning('لطفاً حداقل یک ردیف معتبر با هزینه و تاریخ پر کنید');
      return;
    }

    const items = validItems.map((it, idx) => ({
      id: `${Date.now()}_${idx}`,
      cost: it.cost || 0,
      date: it.date ? it.date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      notes: it.notes || '',
    }));

    setCostEntries((prev) => [...items, ...prev]);
    costForm.resetFields();
    message.success('اطلاعات هزینه ذخیره شد');
  };

  const handleOpenNoteModal = (index) => {
    const currentItems = usageForm.getFieldValue('items') || [];
    const currentNote = currentItems[index]?.notes || '';
    setCurrentNote(currentNote);
    setCurrentNoteIndex(index);
    setNoteModalVisible(true);
  };

  const handleSaveNote = () => {
    const currentItems = usageForm.getFieldValue('items') || [];
    const updatedItems = [...currentItems];
    if (currentNoteIndex !== null) {
      updatedItems[currentNoteIndex] = {
        ...updatedItems[currentNoteIndex],
        notes: currentNote
      };
      usageForm.setFieldsValue({ items: updatedItems });
    }
    setNoteModalVisible(false);
    setCurrentNote('');
    setCurrentNoteIndex(null);
    message.success('یادداشت ذخیره شد');
  };

  const handleOpenCostNoteModal = (index) => {
    const currentItems = costForm.getFieldValue('items') || [];
    const currentNote = currentItems[index]?.notes || '';
    setCurrentCostNote(currentNote);
    setCurrentCostNoteIndex(index);
    setCostNoteModalVisible(true);
  };

  const handleSaveCostNote = () => {
    const currentItems = costForm.getFieldValue('items') || [];
    const updatedItems = [...currentItems];
    if (currentCostNoteIndex !== null) {
      updatedItems[currentCostNoteIndex] = {
        ...updatedItems[currentCostNoteIndex],
        notes: currentCostNote
      };
      costForm.setFieldsValue({ items: updatedItems });
    }
    setCostNoteModalVisible(false);
    setCurrentCostNote('');
    setCurrentCostNoteIndex(null);
    message.success('یادداشت ذخیره شد');
  };

  // تابع برای افزودن ردیف جدید به فرم
  const addUsageRow = (add) => {
    add({
      equipment: undefined,
      quantity: 1,
      date: dayjs(),
      notes: ''
    });
  };

  // تابع برای افزودن ردیف جدید به فرم هزینه
  const addCostRow = (add) => {
    add({
      cost: 0,
      date: dayjs(),
      notes: ''
    });
  };

  const formatDescription = (text) => {
    if (!text) {
      return (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: '#bfbfbf'
        }}>
          <PaperClipOutlined style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.5 }} />
          <div>No description provided</div>
        </div>
      );
    }

    return text.split('\n').map((line, index) => {
      if (line.trim().startsWith('# ')) {
        return (
          <h3 key={index} style={{
            margin: '16px 0 8px 0',
            color: '#262626',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            {line.replace('# ', '')}
          </h3>
        );
      } else if (line.trim().startsWith('## ')) {
        return (
          <h4 key={index} style={{
            margin: '12px 0 6px 0',
            color: '#434343',
            fontSize: '16px',
            fontWeight: '500'
          }}>
            {line.replace('## ', '')}
          </h4>
        );
      } else if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
        return (
          <div key={index} style={{
            margin: '4px 0',
            paddingLeft: '20px',
            position: 'relative'
          }}>
            <span style={{
              position: 'absolute',
              left: '8px',
              color: '#1890ff'
            }}>•</span>
            {line.replace(/^[-*] /, '')}
          </div>
        );
      } else if (/^\d+\./.test(line.trim())) {
        return (
          <div key={index} style={{
            margin: '4px 0',
            paddingLeft: '20px',
            position: 'relative'
          }}>
            <span style={{
              position: 'absolute',
              left: '8px',
              color: '#52c41a',
              fontWeight: '500'
            }}>
              {line.match(/^(\d+)\./)[1]}.
            </span>
            {line.replace(/^\d+\./, '')}
          </div>
        );
      } else if (line.trim() === '') {
        return <br key={index} />;
      } else {
        return (
          <div key={index} style={{
            margin: '8px 0',
            lineHeight: '1.6'
          }}>
            {line}
          </div>
        );
      }
    });
  };

  if (!task) return null;

  const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.Medium;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <PaperClipOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
          <div>
            <Title level={4} style={{ margin: 0, color: '#1890ff', fontSize: '16px' }}>
              Task Details
            </Title>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              Complete information about this task
            </Text>
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={modalWidth}
      centered
      style={{ top: 20, maxWidth: '95vw' }}
      bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      footer={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              Created {getRelativeTime(task.createdAt)} • Updated {getRelativeTime(task.updatedAt)}
            </Text>
          </div>
          <Space>
            <Button
              icon={<EditOutlined />}
              onClick={() => { onEdit(task); onClose(); }}
              size="middle"
              style={{
                borderRadius: '6px',
                padding: '0 14px',
                height: '36px'
              }}
            >
              Edit Task
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              size="middle"
              style={{
                borderRadius: '6px',
                padding: '0 14px',
                height: '36px'
              }}
            >
              Delete Task
            </Button>
          </Space>
        </Space>
      }
    >
      {/* Header with Key Information */}
      <Card
        style={{
          marginBottom: '16px',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          border: 'none'
        }}
        bodyStyle={{ padding: '16px' }}
      >
        <Row gutter={[12, 12]} align="middle" style={{ overflowX: 'hidden' }}>
          <Col xs={24} md={16}>
            <Title level={3} style={{ margin: 0, marginBottom: '6px', fontSize: '18px' }}>
              {task.content}
            </Title>
            {task.description && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {taskStats.lines} lines • {taskStats.words} words • {taskStats.characters} chars
                </Text>
              </div>
            )}
          </Col>
          <Col xs={24} md={8}>
            <div style={{ textAlign: 'right' }}>
              <Tag
                style={{
                  background: priority.bgColor,
                  color: priority.color,
                  border: `1px solid ${priority.borderColor}`,
                  padding: '2px 10px',
                  borderRadius: '16px',
                  fontWeight: '600',
                  fontSize: '11px',
                  marginBottom: '6px'
                }}
              >
                <FlagOutlined style={{ marginRight: '4px' }} />
                {task.priority} Priority
              </Tag>
              <div>
                <Rate
                  disabled
                  value={task.difficulty}
                  character={<StarOutlined />}
                  style={{ fontSize: '14px', color: '#faad14' }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Enhanced Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setTab}
        size="small"
        style={{ marginBottom: '12px' }}
        items={[
          {
            key: 'details',
            label: (
              <span>
                <PaperClipOutlined />
                Details
              </span>
            ),
          },
          {
            key: 'description',
            label: (
              <span>
                <PaperClipOutlined />
                Description
                {task.description && (
                  <Badge
                    count={taskStats.lines}
                    size="small"
                    style={{
                      marginLeft: '6px',
                      background: '#1890ff'
                    }}
                  />
                )}
              </span>
            ),
          },
          {
            key: 'checklist',
            label: (
              <span>
                <CheckCircleOutlined />
                Checklist
                <Badge
                  count={subtasks.length}
                  size="small"
                  style={{
                    marginLeft: '6px',
                    background: '#52c41a'
                  }}
                />
              </span>
            ),
          },
          {
            key: 'attachments',
            label: (
              <span>
                <PaperClipOutlined />
                Files
                <Badge
                  count={task.attachments?.length || 0}
                  size="small"
                  style={{
                    marginLeft: '6px',
                    background: '#fa8c16'
                  }}
                />
              </span>
            ),
          },
          {
            key: 'comments',
            label: (
              <span>
                <MessageOutlined />
                Comments
                <Badge
                  count={comments.length}
                  size="small"
                  style={{
                    marginLeft: '6px',
                    background: '#722ed1'
                  }}
                />
              </span>
            ),
          },
          {
            key: 'usage',
            label: (
              <span>
                <TagOutlined />
                Equipment Usage
                <Badge
                  count={usageEntries.length}
                  size="small"
                  style={{
                    marginLeft: '6px',
                    background: '#13c2c2'
                  }}
                />
              </span>
            ),
          },
          {
            key: 'costs',
            label: (
              <span>
                <DollarOutlined />
                Costs
                <Badge
                  count={costEntries.length}
                  size="small"
                  style={{
                    marginLeft: '6px',
                    background: '#52c41a'
                  }}
                />
              </span>
            ),
          }
        ]}
      />

      {/* Tab Content */}
      {activeTab === 'details' && (
        <Row gutter={[16, 16]} style={{ overflowX: 'hidden' }}>
          <Col xs={24} md={16} style={{ minWidth: 0 }}>
            <Card
              title="Task Information"
              style={{ marginBottom: '16px', maxWidth: '100%', overflowX: 'hidden' }}
              extra={
                <Button
                  icon={<EditOutlined />}
                  onClick={() => { onEdit(task); onClose(); }}
                  size="small"
                >
                  Edit
                </Button>
              }
            >
              <Descriptions column={1} bordered size="middle" style={{ width: '100%' }}>
                <Descriptions.Item 
                  label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <UserOutlined />
                    <span>Assigned To</span>
                  </div>
                }
                  labelStyle={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                  contentStyle={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <Avatar size="small" style={{ backgroundColor: '#1890ff' }}>
                      {task.assignee.charAt(0)}
                    </Avatar>
                    <span style={{ overflowWrap: 'anywhere' }}>{task.assignee}</span>
                  </div>
                </Descriptions.Item>

                <Descriptions.Item 
                  label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CalendarOutlined />
                    <span>Time Frame</span>
                  </div>
                }
                  labelStyle={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                  contentStyle={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ overflowWrap: 'anywhere' }}>{formatDate(task.startDate)} to {formatDate(task.endDate)}</div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {taskStats.duration} days total
                    </Text>
                  </div>
                </Descriptions.Item>

                <Descriptions.Item 
                  label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TagOutlined />
                    <span>Labels</span>
                  </div>
                }
                  labelStyle={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                  contentStyle={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                >
                  {task.labels?.map(label => (
                    <Tag
                      key={label}
                      style={{
                        background: '#f0f0f0',
                        border: 'none',
                        borderRadius: '12px',
                        padding: '2px 8px',
                        marginBottom: '4px',
                        maxWidth: '100%'
                      }}
                    >
                      <span style={{ overflowWrap: 'anywhere' }}>{label}</span>
                    </Tag>
                  ))}
                </Descriptions.Item>

                <Descriptions.Item 
                  label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TagOutlined />
                    <span>Equipments</span>
                  </div>
                }
                  labelStyle={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                  contentStyle={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                >
                  {Array.isArray(task.equipmentIds) && task.equipmentIds.length > 0 ? (
                    <Space wrap>
                      {task.equipmentIds.map(id => (
                        <Tag key={id} color="geekblue">
                          {equipmentValueToLabel.get(id) || `ID: ${id}`}
                        </Tag>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary">-</Text>
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            {/* Subtasks Progress */}
            {subtasks.length > 0 && (
              <Card title="Checklist Progress" style={{ marginBottom: '16px' }}>
                <div style={{ textAlign: 'center' }}>
                  <Progress
                    type="circle"
                    percent={taskStats.subtaskProgress}
                    width={60}
                    format={() => (
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                          {taskStats.completedSubtasks}/{taskStats.totalSubtasks}
                        </div>
                      </div>
                    )}
                  />
                  <div style={{ marginTop: '8px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {taskStats.subtaskProgress}% completed
                    </Text>
                  </div>
                </div>
              </Card>
            )}

            <Card title="Quick Actions">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  type="primary"
                  block
                  onClick={() => { onEdit(task); onClose(); }}
                  style={{ borderRadius: '6px' }}
                >
                  <EditOutlined />
                  Edit Task
                </Button>
                <Button
                  block
                  style={{ borderRadius: '6px' }}
                  onClick={() => setTab('checklist')}
                >
                  <CheckCircleOutlined />
                  Manage Checklist
                </Button>
                <Button
                  block
                  style={{ borderRadius: '6px' }}
                  onClick={() => setTab('description')}
                >
                  <PaperClipOutlined />
                  View Description
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      )}

      {activeTab === 'description' && (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PaperClipOutlined />
              <span>Task Description</span>
              {task.description && (
                <Badge
                  count={`${taskStats.lines} lines`}
                  style={{
                    marginLeft: '8px',
                    background: '#1890ff'
                  }}
                />
              )}
            </div>
          }
          extra={
            <Space>
              {task.description && (
                <Button
                  icon={<CopyOutlined />}
                  onClick={copyDescription}
                  size="small"
                >
                  Copy
                </Button>
              )}
              <Button
                icon={descriptionExpanded ? <CompressOutlined /> : <ExpandOutlined />}
                onClick={toggleDescriptionExpanded}
                size="small"
              >
                {descriptionExpanded ? 'Collapse' : 'Expand'}
              </Button>
              <Button
                icon={<EditOutlined />}
                onClick={() => { onEdit(task); onClose(); }}
                size="small"
                type="primary"
              >
                Edit
              </Button>
            </Space>
          }
        >
          <div
            style={{
              background: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '24px',
              minHeight: descriptionExpanded ? '500px' : '300px',
              maxHeight: descriptionExpanded ? '600px' : '400px',
              overflowY: 'auto',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              fontSize: '14px',
              lineHeight: '1.6',
              color: '#333'
            }}
          >
            {formatDescription(task.description)}
          </div>

          {task.description && (
            <div style={{
              marginTop: '16px',
              padding: '12px 16px',
              background: '#e6f7ff',
              borderRadius: '6px',
              border: '1px solid #91d5ff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  📊 <strong>Description Statistics:</strong> {taskStats.lines} lines,
                  {taskStats.words} words, {taskStats.characters} characters
                </Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Last updated {getRelativeTime(task.updatedAt)}
                </Text>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Checklist Tab */}
      {activeTab === 'checklist' && (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircleOutlined />
              <span>Checklist & Subtasks</span>
              <Badge
                count={`${taskStats.completedSubtasks}/${taskStats.totalSubtasks}`}
                style={{
                  marginLeft: '8px',
                  background: '#52c41a'
                }}
              />
            </div>
          }
          extra={
            <Space>
              <Button
                icon={<PlusOutlined />}
                onClick={() => document.getElementById('new-subtask-input')?.focus()}
                size="small"
              >
                Add Subtask
              </Button>
            </Space>
          }
        >
          {/* Progress Summary */}
          {subtasks.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <Text strong>Overall Progress</Text>
                <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
                  {taskStats.subtaskProgress}%
                </Text>
              </div>
              <Progress
                percent={taskStats.subtaskProgress}
                strokeColor={{
                  '0%': '#1890ff',
                  '100%': '#52c41a',
                }}
                style={{ marginBottom: '8px' }}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {taskStats.completedSubtasks} of {taskStats.totalSubtasks} tasks completed
              </Text>
            </div>
          )}

          {/* Add New Subtask */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <Input
              id="new-subtask-input"
              placeholder="Add a new subtask..."
              value={newSubtask}
              onChange={(e) => setNewSubtask(e.target.value)}
              style={{ flex: 1, borderRadius: '6px' }}
              onPressEnter={addSubtask}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={addSubtask}
              disabled={!newSubtask.trim()}
            >
              Add
            </Button>
          </div>

          {/* Subtasks List */}
          {subtasks.length > 0 ? (
            <List
              dataSource={subtasks}
              renderItem={(subtask) => (
                <List.Item
                  actions={[
                    <Button
                      type="text"
                      danger
                      icon={<DeleteFilled />}
                      onClick={() => deleteSubtask(subtask.id)}
                      size="small"
                    />
                  ]}
                  style={{
                    padding: '12px 16px',
                    border: '1px solid #f0f0f0',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    background: subtask.completed ? '#f6ffed' : 'white'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', width: '100%' }}>
                    <Checkbox
                      checked={subtask.completed}
                      onChange={() => toggleSubtask(subtask.id)}
                      style={{ marginTop: '2px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <Text
                        style={{
                          textDecoration: subtask.completed ? 'line-through' : 'none',
                          color: subtask.completed ? '#8c8c8c' : '#262626',
                          fontSize: '14px',
                          lineHeight: '1.4'
                        }}
                      >
                        {subtask.content}
                      </Text>
                      {subtask.completed && (
                        <div style={{ marginTop: '4px' }}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            ✅ Completed
                          </Text>
                        </div>
                      )}
                    </div>
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#bfbfbf'
            }}>
              <CheckCircleOutlined style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
              <div style={{ fontSize: '16px', marginBottom: '8px', color: '#8c8c8c' }}>
                No subtasks yet
              </div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Add subtasks to break down this task into smaller, manageable steps
              </Text>
            </div>
          )}
        </Card>
      )}

      {/* Equipment Usage Tab */}
      {activeTab === 'usage' && (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TagOutlined />
              <span>Equipment Usage (Self Report)</span>
              <Badge
                count={usageEntries.length}
                style={{ marginLeft: '8px', background: '#13c2c2' }}
              />
            </div>
          }
        >
          <Form 
            form={usageForm} 
            layout="vertical" 
            onFinish={handleSubmitUsage}
            initialValues={{ items: [{}] }}
          >
            <Form.List name="items">
              {(fields, { add, remove }) => (
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  {fields.map((field, index) => (
                    <Row key={field.key} gutter={8} wrap>
                      <Col xs={24} md={8}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'equipment']}
                          label={index === 0 ? 'Equipment' : ''}
                          rules={[{ required: true, message: 'Please select equipment' }]}
                        >
                          <Select
                            showSearch
                            placeholder="Select equipment"
                            options={equipmentOptions}
                            filterOption={(input, option) =>
                              (option?.label || '').toLowerCase().includes(input.toLowerCase())
                            }
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={4}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'quantity']}
                          label={index === 0 ? 'Quantity' : ''}
                          initialValue={1}
                        >
                          <InputNumber 
                            min={1} 
                            max={1000} 
                            placeholder="Qty" 
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={6}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'date']}
                          label={index === 0 ? 'Date' : ''}
                          rules={[{ required: true, message: 'Please select date' }]}
                          initialValue={dayjs()}
                        >
                          <DatePicker 
                            style={{ width: '100%' }} 
                            format="YYYY-MM-DD"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={4} style={{ display: 'flex', alignItems: index === 0 ? 'flex-end' : 'center' }}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'notes']}
                          label={index === 0 ? 'Notes' : ''}
                          style={{ display: 'none' }}
                        >
                          <Input />
                        </Form.Item>
                        <Button 
                          type="default" 
                          onClick={() => handleOpenNoteModal(index)}
                          block
                          icon={<FileTextOutlined />}
                        >
                          Notes
                        </Button>
                      </Col>
                      <Col xs={24} md={2} style={{ display: 'flex', alignItems: index === 0 ? 'flex-end' : 'center' }}>
                        <Button 
                          danger 
                          onClick={() => remove(field.name)} 
                          disabled={fields.length === 1} 
                          block
                        >
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <div>
                    <Space>
                      <Button 
                        type="dashed" 
                        onClick={() => addUsageRow(add)}
                        icon={<PlusOutlined />}
                      >
                        Add Row
                      </Button>
                      <Button type="primary" htmlType="submit">
                        Save Usage
                      </Button>
                    </Space>
                  </div>
                </Space>
              )}
            </Form.List>
          </Form>

          {/* Note Modal */}
          <Modal
            title="Add Notes"
            open={noteModalVisible}
            onOk={handleSaveNote}
            onCancel={() => {
              setNoteModalVisible(false);
              setCurrentNote('');
              setCurrentNoteIndex(null);
            }}
            okText="Save Note"
            cancelText="Cancel"
            width={600}
          >
            <TextArea
              rows={6}
              value={currentNote}
              onChange={(e) => setCurrentNote(e.target.value)}
              placeholder="Enter your notes here..."
              style={{ marginTop: '16px' }}
            />
          </Modal>

          {/* Saved entries */}
          <div style={{ marginTop: '16px' }}>
            <Table
              size="small"
              rowKey="id"
              columns={[
                { 
                  title: 'Equipment', 
                  dataIndex: 'equipmentName', 
                  key: 'equipmentName',
                  render: (name) => name || '-'
                },
                { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
                { title: 'Date', dataIndex: 'date', key: 'date' },
                { 
                  title: 'Notes', 
                  dataIndex: 'notes', 
                  key: 'notes',
                  render: (notes) => notes ? (
                    <Text style={{ maxWidth: '200px' }} ellipsis={{ tooltip: notes }}>
                      {notes}
                    </Text>
                  ) : '-'
                },
              ]}
              dataSource={usageEntries}
              pagination={{ pageSize: 5 }}
            />
          </div>
        </Card>
      )}

      {/* Costs Tab */}
      {activeTab === 'costs' && (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DollarOutlined />
              <span>Costs</span>
              <Badge
                count={costEntries.length}
                style={{ marginLeft: '8px', background: '#52c41a' }}
              />
            </div>
          }
        >
          <Form 
            form={costForm} 
            layout="vertical" 
            onFinish={handleSubmitCost}
            initialValues={{ items: [{}] }}
          >
            <Form.List name="items">
              {(fields, { add, remove }) => (
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  {fields.map((field, index) => (
                    <Row key={field.key} gutter={8} wrap>
                      <Col xs={24} md={6}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'cost']}
                          label={index === 0 ? 'Cost ($)' : ''}
                          rules={[{ required: true, message: 'Please enter cost amount' }]}
                        >
                          <InputNumber 
                            min={0} 
                            max={1000000} 
                            placeholder="0.00" 
                            style={{ width: '100%' }}
                            prefix={<DollarOutlined />}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={6}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'date']}
                          label={index === 0 ? 'Date' : ''}
                          rules={[{ required: true, message: 'Please select date' }]}
                          initialValue={dayjs()}
                        >
                          <DatePicker 
                            style={{ width: '100%' }} 
                            format="YYYY-MM-DD"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={4} style={{ display: 'flex', alignItems: index === 0 ? 'flex-end' : 'center' }}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'notes']}
                          label={index === 0 ? 'Notes' : ''}
                          style={{ display: 'none' }}
                        >
                          <Input />
                        </Form.Item>
                        <Button 
                          type="default" 
                          onClick={() => handleOpenCostNoteModal(index)}
                          block
                          icon={<FileTextOutlined />}
                        >
                          Notes
                        </Button>
                      </Col>
                      <Col xs={24} md={2} style={{ display: 'flex', alignItems: index === 0 ? 'flex-end' : 'center' }}>
                        <Button 
                          danger 
                          onClick={() => remove(field.name)} 
                          disabled={fields.length === 1} 
                          block
                        >
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <div>
                    <Space>
                      <Button 
                        type="dashed" 
                        onClick={() => addCostRow(add)}
                        icon={<PlusOutlined />}
                      >
                        Add Row
                      </Button>
                      <Button type="primary" htmlType="submit">
                        Save Costs
                      </Button>
                    </Space>
                  </div>
                </Space>
              )}
            </Form.List>
          </Form>

          {/* Cost Note Modal */}
          <Modal
            title="Add Notes"
            open={costNoteModalVisible}
            onOk={handleSaveCostNote}
            onCancel={() => {
              setCostNoteModalVisible(false);
              setCurrentCostNote('');
              setCurrentCostNoteIndex(null);
            }}
            okText="Save Note"
            cancelText="Cancel"
            width={600}
          >
            <TextArea
              rows={6}
              value={currentCostNote}
              onChange={(e) => setCurrentCostNote(e.target.value)}
              placeholder="Enter your notes here..."
              style={{ marginTop: '16px' }}
            />
          </Modal>

          {/* Saved cost entries */}
          <div style={{ marginTop: '16px' }}>
            <Table
              size="small"
              rowKey="id"
              columns={[
                { 
                  title: 'Cost', 
                  dataIndex: 'cost', 
                  key: 'cost',
                  render: (cost) => cost ? `$${cost}` : '-'
                },
                { title: 'Date', dataIndex: 'date', key: 'date' },
                { 
                  title: 'Notes', 
                  dataIndex: 'notes', 
                  key: 'notes',
                  render: (notes) => notes ? (
                    <Text style={{ maxWidth: '200px' }} ellipsis={{ tooltip: notes }}>
                      {notes}
                    </Text>
                  ) : '-'
                },
              ]}
              dataSource={costEntries}
              pagination={{ pageSize: 5 }}
              summary={() => (
                <Table.Summary>
                  <Table.Summary.Row style={{ background: '#fafafa' }}>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <Text strong>Total Cost:</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      <Text strong>
                        ${costEntries.reduce((total, entry) => total + (parseFloat(entry.cost) || 0), 0).toFixed(2)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </div>
        </Card>
      )}

      {/* Attachments Tab */}
      {activeTab === 'attachments' && (
        <FileUploader
          attachments={task?.attachments || []}
          onFileUpload={() => {
            message.info('File upload functionality needs implementation');
          }}
          onRemoveFile={(fileId) => {
            message.info('File removal functionality needs implementation');
          }}
        />
      )}

      {/* Comments Tab */}
      {activeTab === 'comments' && (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MessageOutlined />
              <span>Comments & Activity</span>
              <Badge
                count={comments.length}
                style={{
                  marginLeft: '8px',
                  background: '#722ed1'
                }}
              />
            </div>
          }
        >
          {/* Add Comment */}
          <div style={{ marginBottom: '24px' }}>
            <TextArea
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              rows={3}
              style={{
                borderRadius: '6px',
                marginBottom: '8px'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="primary"
                onClick={addComment}
                disabled={!newComment.trim()}
                style={{
                  borderRadius: '6px'
                }}
              >
                Add Comment
              </Button>
            </div>
          </div>

          {/* Comments List */}
          {comments.length > 0 ? (
            <List
              dataSource={comments}
              renderItem={(comment) => (
                <List.Item
                  style={{
                    padding: '16px 0',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        size="large"
                        style={{ backgroundColor: '#1890ff' }}
                        icon={<UserOutlined />}
                      >
                        {comment.avatar}
                      </Avatar>
                    }
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong>{comment.author}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {getRelativeTime(comment.timestamp)}
                        </Text>
                      </div>
                    }
                    description={
                      <div>
                        <Paragraph style={{ margin: '8px 0 0 0', fontSize: '14px', lineHeight: '1.5' }}>
                          {comment.text}
                        </Paragraph>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#bfbfbf'
            }}>
              <MessageOutlined style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
              <div style={{ fontSize: '16px', marginBottom: '8px', color: '#8c8c8c' }}>
                No comments yet
              </div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Start a conversation by adding the first comment
              </Text>
            </div>
          )}
        </Card>
      )}
    </Modal>
  );
};

export default TaskDetailsModalRefactored;